/**
 * Apartado admin · WhatsApp del negocio.  (spec 052)
 *
 * No sustituye /mcp-facebook. No desvincula. No reconecta Sergio ni AIonRoot.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'

import { useSesion } from '../../auth'
import { Button } from '../../components/ui/button'
import { api } from '../../lib/api'
import { fechaHoraLarga } from '../../lib/tiempo'
import type { LineaPadron, PadronWhatsapp } from '../../lib/whatsapp'

export function WhatsappAdmin() {
    const { data: sesion } = useSesion()
    const qc = useQueryClient()
    const [elegida, setElegida] = useState<string>('')
    const [negocio, setNegocio] = useState<string>('')
    const [apiKey, setApiKey] = useState('')
    const [aviso, setAviso] = useState<string | null>(null)
    const [webhook, setWebhook] = useState<{ callback: string | null; verifyToken: string | null } | null>(
        null,
    )

    const padron = useQuery({
        queryKey: ['whatsapp', 'padron'],
        queryFn: () => api.get<PadronWhatsapp>('/whatsapp/padron'),
        enabled: sesion?.role === 'admin',
    })

    const tokenSesion = useQuery({
        queryKey: ['whatsapp', 'token-sesion'],
        queryFn: () => api.get<{ apiKey: string | null }>('/whatsapp/token-sesion'),
        enabled: sesion?.role === 'admin' && padron.data?.sesionMeta === true,
    })

    const vincular = useMutation({
        mutationFn: () => {
            const linea = padron.data?.lineas.find((l) => l.e164 === elegida)
            if (!linea) throw new Error('elige una línea libre')
            const accountId = Number(negocio)
            if (!Number.isInteger(accountId) || accountId < 1) throw new Error('elige un negocio')
            const key = apiKey.trim() || tokenSesion.data?.apiKey || ''
            return api.post<{
                ok: true
                modo: string
                inboxId?: number
                callback?: string | null
                verifyToken?: string | null
            }>('/whatsapp/vincular', {
                accountId,
                phoneNumber: linea.e164,
                phoneNumberId: linea.phoneNumberId ?? '',
                businessAccountId: linea.businessAccountId ?? '',
                ...(key ? { apiKey: key } : { usarTokenSesion: true }),
            })
        },
        onSuccess: (r) => {
            setAviso(
                r.modo === 'reanudado'
                    ? 'Credenciales aportadas. El aprovisionador reanuda el tramo WhatsApp.'
                    : 'Inbox WhatsApp creado y AgentBot conectado.',
            )
            setWebhook({
                callback: r.callback ?? null,
                verifyToken: r.verifyToken ?? null,
            })
            void qc.invalidateQueries({ queryKey: ['whatsapp'] })
            void qc.invalidateQueries({ queryKey: ['altas'] })
        },
        onError: (err: Error) => setAviso(err.message),
    })

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    const lineas = padron.data?.lineas ?? []
    const libres = lineas.filter((l) => l.libre)
    const conectadas = lineas.filter((l) => !l.libre)
    const destinos = padron.data?.negociosSinCanal ?? []

    return (
        <div className="mx-auto max-w-3xl space-y-8" data-testid="whatsapp-admin">
            <header className="space-y-2">
                <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                    ← Operación
                </Link>
                <h1 className="text-lg font-semibold text-neutral-100">WhatsApp</h1>
                <p className="text-sm text-neutral-400">
                    Líneas ya dadas de alta en Meta y las que Chatwoot ya tiene. Vincular crea el
                    inbox y enseña el callback para pegarlo en Cloud API. No abre Chatwoot. MCP
                    Facebook sigue en{' '}
                    <Link to="/mcp-facebook" className="text-sky-300 hover:underline">
                        /mcp-facebook
                    </Link>
                    .
                </p>
                {padron.data?.sesionMeta && padron.data.metaRefrescadoEn == null ? (
                    <p className="text-xs text-neutral-500">
                        Las líneas de Meta no se piden al abrir. Salen del último «Actualizar desde
                        Facebook» en MCP Facebook.
                    </p>
                ) : padron.data?.metaRefrescadoEn ? (
                    <p className="text-xs text-neutral-500">
                        Líneas de Meta del {fechaHoraLarga(padron.data.metaRefrescadoEn)}. Esta
                        pantalla no vuelve a preguntar.
                    </p>
                ) : null}
            </header>

            {aviso ? (
                <p className="text-sm text-amber-200/90" data-testid="whatsapp-aviso">
                    {aviso}
                </p>
            ) : null}

            {webhook ? <FichaWebhook callback={webhook.callback} verifyToken={webhook.verifyToken} /> : null}

            <section className="space-y-3" aria-label="Líneas conectadas">
                <h2 className="text-sm font-semibold text-neutral-200">Conectadas</h2>
                {padron.isPending ? (
                    <p className="text-sm text-neutral-500">Cargando…</p>
                ) : conectadas.length === 0 ? (
                    <p className="text-sm text-neutral-500">Ninguna conectada en Chatwoot.</p>
                ) : (
                    <ul className="space-y-3">
                        {conectadas.map((l) => (
                            <li
                                key={l.e164}
                                className="border border-neutral-800 p-3 text-sm"
                                data-testid={`linea-conectada-${l.accountId ?? l.e164}`}
                            >
                                <p className="font-medium text-neutral-100">{l.e164}</p>
                                <p className="text-xs text-neutral-500">
                                    {l.negocio ?? 'negocio'}
                                    {l.accountId != null ? ` · cuenta ${l.accountId}` : ''}
                                </p>
                                <FichaWebhook callback={l.callback} verifyToken={l.verifyToken} />
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="space-y-3" aria-label="Vincular línea libre">
                <h2 className="text-sm font-semibold text-neutral-200">Vincular una línea libre</h2>
                <p className="text-xs text-neutral-500">
                    Un número, un negocio. Sergio y AIonRoot no se reconectan. Pegar el token de
                    Cloud API si no hay sesión Meta.
                </p>
                {libres.length === 0 ? (
                    <p className="text-sm text-neutral-500">
                        No hay líneas libres. El número se da de alta en Meta a mano; aquí se vincula.
                    </p>
                ) : destinos.length === 0 ? (
                    <p className="text-sm text-neutral-500">
                        No hay negocios sin canal WhatsApp a los que se pueda vincular.
                    </p>
                ) : (
                    <form
                        className="grid gap-3"
                        onSubmit={(e) => {
                            e.preventDefault()
                            vincular.mutate()
                        }}
                    >
                        <label className="block text-xs text-neutral-400">
                            Línea
                            <select
                                className="mt-1 w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-sm text-neutral-100"
                                value={elegida}
                                onChange={(e) => setElegida(e.target.value)}
                                required
                            >
                                <option value="">Elegir…</option>
                                {libres.map((l) => (
                                    <option key={l.e164} value={l.e164}>
                                        {etiquetaLinea(l)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block text-xs text-neutral-400">
                            Negocio
                            <select
                                className="mt-1 w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-sm text-neutral-100"
                                value={negocio}
                                onChange={(e) => setNegocio(e.target.value)}
                                required
                            >
                                <option value="">Elegir…</option>
                                {destinos.map((n) => (
                                    <option key={n.accountId} value={n.accountId}>
                                        {n.name} · {n.estadoAlta}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block text-xs text-neutral-400">
                            API key de Cloud API
                            <input
                                className="mt-1 w-full border border-neutral-700 bg-neutral-950 px-2 py-1 text-sm text-neutral-100"
                                type="password"
                                autoComplete="off"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={
                                    tokenSesion.data?.apiKey
                                        ? 'Hay token de sesión Meta; se usará si dejas esto vacío'
                                        : 'Token permanente de Meta'
                                }
                            />
                        </label>
                        <div>
                            <Button type="submit" disabled={vincular.isPending}>
                                {vincular.isPending ? 'Vinculando…' : 'Vincular'}
                            </Button>
                        </div>
                    </form>
                )}
            </section>
        </div>
    )
}

function etiquetaLinea(l: LineaPadron): string {
    return l.phoneNumberId ? `${l.e164} · ${l.phoneNumberId}` : l.e164
}

function FichaWebhook({
    callback,
    verifyToken,
}: {
    callback: string | null
    verifyToken: string | null
}) {
    if (!callback && verifyToken === null) return null
    return (
        <div className="mt-2 space-y-1 text-xs" data-testid="whatsapp-webhook">
            {callback ? (
                <p>
                    <span className="text-neutral-500">Callback </span>
                    <code className="break-all text-neutral-200">{callback}</code>
                </p>
            ) : null}
            {verifyToken ? (
                <p>
                    <span className="text-neutral-500">Verify token </span>
                    <code className="break-all text-neutral-200">{verifyToken}</code>
                </p>
            ) : (
                <p className="text-neutral-500">
                    Chatwoot no devolvió el verify token. No se inventa: cópialo del inbox si Meta
                    lo pide.
                </p>
            )}
        </div>
    )
}
